import { HttpClient } from '@angular/common/http';
import { inject, Injectable, Service } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { IPagedRequest } from '../interfaces/paged-request.interface';
import { ChannelDTO } from '../models/channel.model';
import { PagedResponse } from '../models/paged-response.model';
import { PointOfSaleDto } from '../models/point-of-sale.model';
import { Shop } from '../models/shop.model';
import { UserInfo } from '../models/user-info.model';
import { LoaderService } from '../services/loader.service';

/**
 * Chiamate HTTP di contesto (utente, stores, canali, POS, tenant).
 * Solo trasporto e normalizzazione della risposta: nessuno stato.
 * Lo stato vive negli store di dominio (vedi ADR 002); la cache di getShops
 * resta nel facade CoreService.
 */
@Service()
export class ContextApiService {
  private readonly _http = inject(HttpClient);
  private readonly _loader = inject(LoaderService);
  private readonly _api = environment.urls.apiUrl.url;

  fetchUserInfo(loaderId?: string): Observable<UserInfo> {
    if (loaderId) this._loader.showLoader(loaderId);
    return this._http.get<UserInfo>(`${this._api}/users/user-info`).pipe(
      map(data => new UserInfo(data)),
      tap(() => {
        if (loaderId) this._loader.dismissLoader(loaderId);
      })
    );
  }

  fetchShops(params?: IPagedRequest): Observable<Shop[]> {
    return this._http.get<PagedResponse<Shop>>(`${this._api}/stores`, {
      params: {
        skipCount: params?.skipCount?.toString() || '0',
        maxResultCount: params?.maxResultCount?.toString() || '0',
      }
    }).pipe(
      map(data => {
        const items = data.items.filter(s => s.uri?.toLowerCase().indexOf('https') >= 0);
        items.sort((a, b) => a.id - b.id);
        return items.map(x => new Shop(x));
      })
    );
  }

  fetchChannels(): Observable<PagedResponse<ChannelDTO>> {
    const params = { maxResultCount: 0, skipCount: 0 };
    return this._http.get<PagedResponse<ChannelDTO>>(this._api + '/channels', { params });
  }

  fetchPointsOfSale(params?: IPagedRequest, externalId?: string): Observable<PagedResponse<PointOfSaleDto>> {
    return this._http.get<PagedResponse<PointOfSaleDto>>(`${this._api}/point-of-sales`, {
      params: {
        skipCount: params?.skipCount?.toString(),
        maxResultCount: params?.maxResultCount?.toString(),
        sorting: params?.sorting,
        externalId: externalId
      }
    }).pipe(
      map(data => {
        data.items = data.items.map(x => new PointOfSaleDto(x));
        return data;
      })
    );
  }

  // Ritorno non tipizzato di proposito: parità con la vecchia firma di
  // CoreService.updateUserCurrentTenant (il body della risposta non viene usato).
  switchTenant(loaderId: string, tenantId: string) {
    if (loaderId) this._loader.showLoader(loaderId);
    return this._http.post(this._api + '/users/switch-tenant/' + tenantId, null).pipe(
      tap(() => {
        if (loaderId) this._loader.dismissLoader(loaderId);
      })
    );
  }
}
